/**
 * Embedded mileage section — a collapsed "🚗 Log mileage" toggle for the
 * Purchase / Sale / Trade / Grading forms. Checking the toggle reveals the
 * trip fields; on save the host form calls saveMileageSection() which creates
 * a mileage row whose Related Deal/Lot is the transaction's ID and whose Date
 * is the transaction's date. When editing a transaction that already has
 * linked trips, a read-only summary is shown (manage them on the Mileage page).
 *
 * Only one modal exists at a time, so the mlgsec_* element ids are safe to
 * share across the four host forms. Inputs carry NO name attribute so the
 * host form's readForm() never sees them.
 */
import { DB } from "../lib/db";
import { Lists, Settings } from "../lib/prefs";
import { escapeHtml, toast } from "../lib/dom";
import { fmt$, fmtN } from "../utils/format";
import { markModalDirty } from "./modal";

function linkedTrips(relatedId: string) {
  if (!relatedId) return [];
  return DB.table("mileage").filter(r => String(r?.["Related Deal/Lot"] || "").trim() === relatedId);
}

/** Section HTML. `relatedId` = the transaction ID the trip will link to;
 *  `defaultPurpose` preselects the Purpose dropdown (e.g. "Trade"). */
export function mileageSectionHtml(relatedId: string, defaultPurpose: string): string {
  const trips = linkedTrips(relatedId);
  const totalMiles = trips.reduce((a, r) => a + (Number(r["Miles"]) || 0), 0);
  const totalDed   = trips.reduce((a, r) => a + (Number(r["Deduction"]) || 0), 0);
  const purposes = Lists.get("Mileage Purpose").slice();
  if (defaultPurpose && !purposes.includes(defaultPurpose)) purposes.unshift(defaultPurpose);
  const rate = Settings.get().irsRate;

  const existingHtml = trips.length ? `
    <div class="text-xs text-[var(--muted)] mb-2">
      🚗 <b>${fmtN(trips.length)}</b> linked trip(s) · <b>${fmtN(totalMiles)}</b> mi · <b>${fmt$(totalDed)}</b> deduction
      <span class="ml-1">(edit or delete them on the Mileage page)</span>
    </div>` : "";

  return `
    <div class="mt-4 pt-3 border-t border-[var(--border)]" id="mlgsec">
      ${existingHtml}
      <label class="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" id="mlgsec_toggle">
        <span>🚗 Log mileage for this transaction${trips.length ? " (adds another trip)" : ""}</span>
      </label>
      <div id="mlgsec_fields" class="hidden mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label class="label text-xs" for="mlgsec_purpose">Purpose</label>
          <select class="select" id="mlgsec_purpose">
            ${purposes.map(p => `<option value="${escapeHtml(p)}" ${p === defaultPurpose ? "selected" : ""}>${escapeHtml(p)}</option>`).join("")}
          </select>
        </div>
        <div>
          <label class="label text-xs" for="mlgsec_start">Start Odometer</label>
          <input class="input" id="mlgsec_start" type="number" step="1">
        </div>
        <div>
          <label class="label text-xs" for="mlgsec_end">End Odometer</label>
          <input class="input" id="mlgsec_end" type="number" step="1">
        </div>
        <div>
          <label class="label text-xs" for="mlgsec_miles">Miles (auto if blank)</label>
          <input class="input" id="mlgsec_miles" type="number" step="0.1">
        </div>
        <div>
          <label class="label text-xs" for="mlgsec_rate">IRS Rate ($/mi)</label>
          <input class="input" id="mlgsec_rate" type="number" step="0.001" value="${rate}">
        </div>
        <div>
          <label class="label text-xs" for="mlgsec_notes">Trip Notes</label>
          <input class="input" id="mlgsec_notes" placeholder="Optional">
        </div>
        <div class="col-span-2 sm:col-span-3 text-xs text-[var(--muted)]">
          Saves as a trip on the Mileage page, dated to this transaction and linked via Related Deal/Lot. Trip date/purpose stay editable there.
        </div>
      </div>
    </div>`;
}

/** Call once after openModal(): wires the toggle + the odometer → miles
 *  auto-calc (same rules as the standalone Mileage form). */
export function wireMileageSection(): void {
  const tog    = document.getElementById("mlgsec_toggle") as HTMLInputElement | null;
  const fields = document.getElementById("mlgsec_fields");
  if (tog && fields) {
    tog.addEventListener("change", () => {
      fields.classList.toggle("hidden", !tog.checked);
      markModalDirty();
    });
  }
  const startInp = document.getElementById("mlgsec_start") as HTMLInputElement | null;
  const endInp   = document.getElementById("mlgsec_end")   as HTMLInputElement | null;
  const milesInp = document.getElementById("mlgsec_miles") as HTMLInputElement | null;
  if (!startInp || !endInp || !milesInp) return;
  let milesManual = false;
  const recompute = () => {
    if (milesManual) return;
    const s = parseFloat(startInp.value);
    const e = parseFloat(endInp.value);
    if (Number.isFinite(s) && Number.isFinite(e)) milesInp.value = String(+(e - s).toFixed(1));
  };
  startInp.addEventListener("input", recompute);
  endInp.addEventListener("input", recompute);
  milesInp.addEventListener("input", () => {
    milesManual = milesInp.value.trim() !== "";
    if (!milesManual) recompute();
  });
}

/** Call from the host form's save fn AFTER the transaction row is written.
 *  Creates the linked mileage row when the toggle is on and miles resolve
 *  to > 0. Never throws — a failed trip write toasts but doesn't undo the
 *  already-saved transaction. Returns true when a trip was created. */
export async function saveMileageSection(relatedId: string, date: string): Promise<boolean> {
  const tog = document.getElementById("mlgsec_toggle") as HTMLInputElement | null;
  if (!tog || !tog.checked) return false;
  const val = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "";
  const start = parseFloat(val("mlgsec_start"));
  const end   = parseFloat(val("mlgsec_end"));
  let miles   = parseFloat(val("mlgsec_miles"));
  if (!Number.isFinite(miles) && Number.isFinite(start) && Number.isFinite(end)) {
    miles = +(end - start).toFixed(1);
  }
  if (!Number.isFinite(miles) || miles <= 0) {
    toast("Mileage toggle was on but no miles entered — no trip logged", "err");
    return false;
  }
  const rate = Number(val("mlgsec_rate")) || Settings.get().irsRate;
  const row: Record<string, any> = {
    "Date":             date,
    "Purpose":          val("mlgsec_purpose"),
    "Start Odo":        Number.isFinite(start) ? start : "",
    "End Odo":          Number.isFinite(end) ? end : "",
    "Miles":            miles,
    "IRS Rate":         rate,
    "Deduction":        +(miles * rate).toFixed(2),
    "Related Deal/Lot": relatedId,
    "Notes":            val("mlgsec_notes"),
  };
  try {
    await DB.add("mileage", row);
  } catch (e: any) {
    console.error("[saveMileageSection]", e);
    toast("Trip save failed: " + (e?.message || e), "err");
    return false;
  }
  toast(`Trip logged · ${fmtN(miles)} mi · ${fmt$(row["Deduction"])} → ${relatedId}`);
  return true;
}
